// ============================================================================
// Widget — Case Pipeline
// Grouped horizontal bar — case counts per stage, coloured by category.
// ============================================================================
import React from 'react';
import ReactECharts from 'echarts-for-react';
import WidgetShell from './WidgetShell';
import { mockCasePipeline, fmtAmount, fmtCount } from '../executiveMockData';

const CATEGORY_COLOUR: Record<string, string> = {
  'Early Recovery': '#2563eb',
  'Recovery':       '#16a34a',
  'Litigation':     '#d97706',
};

const CasePipelineWidget: React.FC = () => {
  // Sort so categories cluster visually.
  const order = ['Early Recovery', 'Recovery', 'Litigation'];
  const sorted = [...mockCasePipeline].sort(
    (a, b) => order.indexOf(a.category) - order.indexOf(b.category),
  );

  const labels  = sorted.map(s => s.stage);
  const counts  = sorted.map(s => s.caseCount);
  const colors  = sorted.map(s => CATEGORY_COLOUR[s.category]);

  const option = {
    grid: { left: 130, right: 20, top: 16, bottom: 28 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any[]) => {
        const idx = params[0].dataIndex;
        const s = sorted[idx];
        return `<strong>${s.category} — ${s.stage}</strong><br/>
                Cases: ${fmtCount(s.caseCount)}<br/>
                Outstanding: ${fmtAmount(s.outstanding)}`;
      },
    },
    xAxis: { type: 'value', axisLabel: { fontSize: 10 }, splitLine: { lineStyle: { color: '#e5e7eb', type: 'dashed' } } },
    yAxis: {
      type: 'category',
      data: labels,
      inverse: true,
      axisTick: { show: false },
      axisLabel: { fontSize: 11 },
    },
    series: [
      {
        type: 'bar',
        barWidth: 14,
        data: counts.map((v, i) => ({ value: v, itemStyle: { color: colors[i], borderRadius: [0, 3, 3, 0] } })),
        label: { show: true, position: 'right', fontSize: 10, color: '#374151' },
      },
    ],
  };

  return (
    <WidgetShell
      title="Case Pipeline"
      subtitle="Open cases by stage, coloured by category"
      action={
        <div style={{ display: 'flex', gap: '0.5rem', fontSize: '0.7rem' }}>
          {order.map(cat => (
            <span key={cat} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: CATEGORY_COLOUR[cat] }} />
              {cat}
            </span>
          ))}
        </div>
      }
    >
      <ReactECharts option={option} style={{ height: 280, width: '100%' }} />
    </WidgetShell>
  );
};

export default CasePipelineWidget;
